import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Col, CustomInput, FormGroup, Input, Label, Row } from "reactstrap";
import moment from "moment";
import FileUploadArea from "../common/FileUploadArea";
import { fetchCurrentAccount, fetchReserve } from "../../helpers/api/finance";
import { CurrencyContext } from "../../context/Context";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import { formatAmountForBackend, formatAmountRounded } from "../../helpers/currencyConversion";

export const emptyExpensePayment = () => ({
  paid: false,
  paymentDate: moment().format("YYYY-MM-DD"),
  source: "",
  usesReserveFund: false,
  reserveFundId: "",
  reference: "",
  description: "",
  file: null,
  rate: null,
});

export const expensePaymentError = (payment, amount) => {
  if (!payment.paid) return null;
  if (!payment.paymentDate) return "Indica la fecha en que se pagó el gasto.";
  if (!payment.source) return "Elige de dónde salió el dinero del pago.";
  if (payment.usesReserveFund && !payment.reserveFundId) return "Elige el fondo de reserva del que salió el pago.";
  if (!payment.rate || Number(payment.rate) <= 0) return "No hay una tasa registrada para la fecha del pago.";
  if (!amount || Number(amount) <= 0) return "El monto del gasto tiene que ser mayor que 0.";
  return null;
};

export const expensePaymentPayload = (payment, amount) => {
  if (!payment.paid) return {};

  return {
    paid: true,
    payment: {
      pay_from_account_current: 1,
      bank_id: payment.source.startsWith("BANK_") ? payment.source.split("_")[1] : null,
      reserve_fund_id: payment.usesReserveFund ? payment.reserveFundId : null,
      payment_date: payment.paymentDate,
      reference: payment.reference || null,
      description: payment.description || null,
      rate: payment.rate,
      amount_bs: formatAmountForBackend(Number(amount) * Number(payment.rate)),
    },
    payment_voucher: payment.file || null,
  };
};

export const ExpensePaidNotice = ({ title, detail }) => (
  <Card className="border-success bg-soft-success mb-3">
    <CardBody className="py-3">
      <div className="text-800 fs--1 font-weight-semi-bold">{title}</div>
      {detail && <div className="text-700 fs--1 mt-1">{detail}</div>}
    </CardBody>
  </Card>
);

ExpensePaidNotice.propTypes = {
  title: PropTypes.string.isRequired,
  detail: PropTypes.string,
};

const SOURCE_LABELS = { CURRENT: "Caja", BANK: "Banco" };

const ExpensePaymentFields = ({ condominiumId, amount, value, onChange, chargedToOwners = true, helpText }) => {
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const [accounts, setAccounts] = useState([]);
  const [reserveFunds, setReserveFunds] = useState([]);
  const [preview, setPreview] = useState(null);

  const update = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    if (!value.paid || !condominiumId) return undefined;

    let isMounted = true;
    fetchCurrentAccount(condominiumId)
      .then((response) => isMounted && setAccounts((response?.data || []).filter((account) => account.type !== "ADVANCES")))
      .catch(() => isMounted && setAccounts([]));
    fetchReserve(condominiumId)
      .then((response) => isMounted && setReserveFunds(response?.data || []))
      .catch(() => isMounted && setReserveFunds([]));

    return () => {
      isMounted = false;
    };
  }, [value.paid, condominiumId]);

  const paymentDate = useMemo(
    () => (value.paymentDate ? moment(value.paymentDate) : null),
    [value.paymentDate]
  );

  const { appliedExchangeRate, isRateLoading } = useExchangeRateForDate({
    currencyId: value.paid ? curr?.currency?.id : null,
    isUsdCurrency: false,
    date: paymentDate,
    activeExchangeRate: Number(curr?.value ?? 0),
    condominiumId,
  });

  useEffect(() => {
    const rate = Number(appliedExchangeRate || 0) > 0 ? Number(formatAmountForBackend(appliedExchangeRate)) : null;
    if (value.paid && rate !== value.rate) {
      onChange({ ...value, rate });
    }
  }, [appliedExchangeRate, value, onChange]);

  const localIso = curr?.currency?.iso || "Bs";
  const amountBs = value.rate ? Number(amount || 0) * Number(value.rate) : null;

  return (
    <div className="border rounded px-3 pt-3 mb-3 bg-white">
      <FormGroup>
        <CustomInput
          type="switch"
          id="expense-already-paid"
          label="Ya está pagado"
          checked={value.paid}
          onChange={(event) => update({ paid: event.target.checked })}
        />
        <p className="fs--1 text-600 mb-0 mt-1">
          {helpText || (chargedToOwners
            ? "Si ya le pagaste al proveedor, registra el pago aquí: la cuenta por pagar queda pagada y no tienes que pagarla después. A los propietarios se les cobra igual en la planilla."
            : "Si ya le pagaste al proveedor, registra el pago aquí: la cuenta por pagar se crea ya pagada y no tienes que pagarla después.")}
        </p>
      </FormGroup>

      {value.paid && (
        <>
          <p className="fs--1 font-weight-semi-bold mb-3">
            {`Se registra el pago del total del gasto: ${baseCurrency?.symbol || "$"} ${formatAmountRounded(amount || 0, 2)}`}
            {amountBs !== null && !isRateLoading
              ? ` · ${localIso} ${formatAmountRounded(amountBs, 2)} a la tasa del ${paymentDate ? paymentDate.format("DD/MM/YYYY") : ""}`
              : ""}
          </p>
          <Row form>
            <Col md={6}>
              <FormGroup>
                <Label for="expense-payment-date">Fecha de pago</Label>
                <Input
                  id="expense-payment-date"
                  type="date"
                  value={value.paymentDate}
                  max={moment().format("YYYY-MM-DD")}
                  onChange={(event) => update({ paymentDate: event.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="expense-payment-source">Origen de fondos</Label>
                <Input
                  id="expense-payment-source"
                  type="select"
                  value={value.source}
                  onChange={(event) => update({ source: event.target.value })}
                >
                  <option value="">Selecciona</option>
                  {accounts.map((account) => (
                    <option
                      key={account.id}
                      value={account.type === "BANK" ? `BANK_${account.condominium_bank_id}` : account.type}
                    >
                      {SOURCE_LABELS[account.type] || account.type}
                      {account.bank_name ? ` - ${account.bank_name}` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <CustomInput
              type="checkbox"
              id="expense-payment-reserve-fund"
              label="Pagar con fondo de reserva"
              checked={value.usesReserveFund}
              onChange={(event) => update({ usesReserveFund: event.target.checked, reserveFundId: "" })}
            />
            {value.usesReserveFund && (
              <Input
                className="mt-2"
                type="select"
                value={value.reserveFundId}
                onChange={(event) => update({ reserveFundId: event.target.value })}
              >
                <option value="">Selecciona el fondo de reserva</option>
                {reserveFunds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.name}
                  </option>
                ))}
              </Input>
            )}
          </FormGroup>

          <Row form>
            <Col md={6}>
              <FormGroup>
                <Label for="expense-payment-reference">Referencia</Label>
                <Input
                  id="expense-payment-reference"
                  value={value.reference}
                  maxLength={255}
                  onChange={(event) => update({ reference: event.target.value })}
                  placeholder="Ej: número de transferencia"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="expense-payment-description">Descripción del pago</Label>
                <Input
                  id="expense-payment-description"
                  value={value.description}
                  maxLength={1500}
                  onChange={(event) => update({ description: event.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <FileUploadArea
              label="Comprobante de pago"
              accept="image/*,application/pdf"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  update({ file });
                  setPreview(URL.createObjectURL(file));
                }
              }}
              preview={preview}
              fileName={value.file?.name}
              onRemove={() => {
                update({ file: null });
                setPreview(null);
              }}
            />
          </FormGroup>
        </>
      )}
    </div>
  );
};

ExpensePaymentFields.propTypes = {
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  chargedToOwners: PropTypes.bool,
  helpText: PropTypes.string,
};


export default ExpensePaymentFields;
